import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

function AnimatedSection({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {children}
    </div>
  );
}

const donationOptions = [
  {
    amount: "RM10",
    title: "Sahabat Mulia",
    desc: "Bekalan makanan 1 hari untuk 1 keluarga yang memerlukan.",
    impact: "1 keluarga makan sehari",
  },
  {
    amount: "RM30",
    title: "Wakil Mulia",
    desc: "Kotak makanan 3 hari untuk keluarga yang memerlukan.",
    impact: "3 hari bekalan makanan",
  },
  {
    amount: "RM50",
    title: "Pelapis Mulia",
    desc: "Kelas tambahan 1 bulan untuk 1 pelajar miskin.",
    impact: "1 pelajar 1 bulan kelas",
  },
  {
    amount: "RM100",
    title: "Penderma Mulia",
    desc: "Pakej bantuan lengkap: makanan + kelengkapan sekolah.",
    impact: "Pakej bantuan lengkap",
  },
  {
    amount: "RM200",
    title: "Dermawan Mulia",
    desc: "Bantuan makanan 1 minggu + ubat-ubatan asas untuk keluarga.",
    impact: "1 minggu + kesihatan",
  },
  {
    amount: "RM500",
    title: "Wira Mulia",
    desc: "Sumbangan besar untuk menyokong satu program penuh.",
    impact: "1 program penuh",
  },
];

const bankDetails = [
  { label: "Nama Akaun", value: "PERTUBUHAN MULIA INSAN MALAYSIA" },
  { label: "Bank", value: "Bank Islam Malaysia Berhad" },
  { label: "No. Akaun", value: "1203 0010 3452 89" },
  { label: "Swift Code", value: "BIMBMYKL" },
];

export default function Donate() {
  return (
    <div className="min-h-screen pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[280px] md:h-[350px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20uplifting%20scene%20of%20hands%20giving%20and%20receiving%20donations%20in%20a%20Malaysian%20community%20setting%20soft%20golden%20lighting%20earth%20tone%20palette%20green%20and%20warm%20accents%20hopeful%20atmosphere%20charity%20photography&width=1600&height=500&seq=donhero&orientation=landscape"
          alt="Sumbangan Mulia Insan"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-teal-900/70" />
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white">
            Derma
          </h1>
          <p className="text-white/80 text-sm md:text-base mt-3 max-w-xl">
            Sumbangan anda adalah nyawa bagi harapan mereka. Setiap ringgit
            membawa perubahan.
          </p>
        </div>
      </section>

      {/* Donation options */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
              Pilihan Sumbangan
            </p>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Pilih Sumbangan Anda
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-lg mx-auto">
              Pilih jumlah yang sesuai dengan kemampuan anda. Setiap sumbangan
              &mdash; besar atau kecil &mdash; membawa erti yang besar.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {donationOptions.map((opt) => (
              <div
                key={opt.amount}
                className="rounded-xl border border-gray-100 p-5 md:p-6 hover:border-teal-200 hover:shadow-sm transition-all bg-white"
              >
                <div className="text-2xl md:text-3xl font-bold text-teal-700 mb-1">
                  {opt.amount}
                </div>
                <div className="text-xs font-semibold text-gray-800 mb-2">
                  {opt.title}
                </div>
                <p className="text-xs md:text-sm text-gray-500 leading-relaxed mb-4">
                  {opt.desc}
                </p>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">
                  <i className="ri-check-line" />
                  {opt.impact}
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </section>

      {/* Bank transfer info */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-earth-50">
        <AnimatedSection className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div>
              <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
                Cara Sumbang
              </p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Pindahan Bank
              </h2>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">
                Anda boleh membuat sumbangan melalui pindahan bank terus ke akaun
                kami. Sila hantarkan resit ke WhatsApp atau email kami untuk
                tujuan rekod dan penghargaan.
              </p>

              <div className="space-y-3 mb-6">
                {bankDetails.map((b) => (
                  <div
                    key={b.label}
                    className="flex items-start gap-3 bg-white rounded-lg p-3 border border-gray-100"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-teal-50 shrink-0 mt-0.5">
                      <i className="ri-bank-line text-teal-600 text-sm" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">{b.label}</div>
                      <div className="text-sm font-medium text-gray-800">
                        {b.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-500">
                <i className="ri-information-line mr-1" />
                Sumbangan anda layak mendapat pelepasan cukai di bawah Seksyen
                44(6) Akta Cukai Pendapatan 1967.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-100">
              <h3 className="font-display text-lg md:text-xl font-bold text-gray-900 mb-4">
                Kaedah Lain
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-50 shrink-0">
                    <i className="ri-smartphone-line text-primary-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      DuitNow / QR Pay
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Imbas kod QR DuitNow kami melalui aplikasi perbankan anda.
                      Hubungi kami untuk mendapatkan kod QR.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-warm-50 shrink-0">
                    <i className="ri-hand-coin-line text-warm-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Sumbangan Fizikal
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Anda juga boleh menghantar barangan (makanan, pakaian,
                      buku) ke pejabat kami. Hubungi kami untuk perincian.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-50 shrink-0">
                    <i className="ri-time-line text-teal-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Sumbangan Berulang
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Sediakan sumbangan bulanan automatik. Hubungi kami untuk
                      maklumat lanjut.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-gray-100">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-700 text-white text-sm font-semibold rounded-full hover:bg-teal-800 transition-colors"
                >
                  Hubungi Kami <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Transparency */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <AnimatedSection className="max-w-3xl mx-auto text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal-50 mx-auto mb-4">
            <i className="ri-shield-check-line text-teal-600 text-2xl" />
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Ketelusan Penuh
          </h2>
          <p className="text-sm md:text-base text-gray-600 leading-relaxed max-w-lg mx-auto">
            Kami komited kepada ketelusan sepenuhnya. 100% sumbangan anda akan
            diagihkan kepada program bantuan. Tiada potongan gaji tinggi atau
            perbelanjaan pentadbiran yang berlebihan. Laporan kewangan tahunan
            kami boleh didapati atas permintaan.
          </p>
        </AnimatedSection>
      </section>
    </div>
  );
}